/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import React, { ReactNode, useEffect, useRef, useState } from "react"

export interface TabProps {
  readonly id: string
  readonly title: string
  readonly children: ReactNode
  readonly errorCount: number
}

export interface TabContainerProps {
  readonly tabs: readonly TabProps[]
  readonly defaultActiveTab?: string
  readonly activeTab?: string
  readonly onTabChange?: (tabId: string) => void
  readonly id?: string
}

const TabContainer: React.FC<Readonly<TabContainerProps>> = ({
  tabs,
  defaultActiveTab,
  activeTab: externalActiveTab,
  onTabChange,
  id,
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState(defaultActiveTab ?? tabs[0].id)

  const activeTab = externalActiveTab ?? internalActiveTab

  const handleTabClick = (tabId: string) => {
    if (externalActiveTab === undefined) {
      setInternalActiveTab(tabId)
    }

    if (onTabChange) {
      onTabChange(tabId)
    }
  }

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isDropdownOpen) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    document.addEventListener("keydown", handleKeyDown)

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [isDropdownOpen])

  let activeTabData = tabs[0]
  for (const tab of tabs) {
    if (tab.id === activeTab) {
      activeTabData = tab
      break
    }
  }
  const activeTabTitle = activeTabData.title

  const desktopTabs: ReactNode[] = []
  for (const tab of tabs) {
    desktopTabs.push(
      <li className="nav-item" key={tab.id}>
        <button
          className={`nav-link ${activeTab === tab.id ? "active" : ""}`}
          onClick={() => {
            handleTabClick(tab.id)
          }}
          type="button"
          id={tab.id}
          aria-controls={tab.id}
        >
          {tab.title}
          {tab.errorCount > 0 && (
            <span
              className="error-indicator"
              title={`Form has ${tab.errorCount} error${tab.errorCount > 1 ? "s" : ""}`}
              aria-live="polite"
            ></span>
          )}
        </button>
      </li>
    )
  }

  const dropdownTabs: ReactNode[] = []
  for (const tab of tabs) {
    dropdownTabs.push(
      <button
        key={tab.id}
        className={`dropdown-item ${activeTab === tab.id ? "active" : ""}`}
        onClick={() => {
          handleTabClick(tab.id)
          setIsDropdownOpen(false)
        }}
      >
        <div className="d-flex align-items-center">
          <span>{tab.title}</span>
          {tab.errorCount > 0 && (
            <span
              className="error-indicator ms-2"
              title={`Form has ${tab.errorCount} error${tab.errorCount > 1 ? "s" : ""}`}
              aria-live="polite"
            ></span>
          )}
        </div>
      </button>
    )
  }

  const tabPanels: ReactNode[] = []
  for (const tab of tabs) {
    tabPanels.push(
      <div
        key={tab.id}
        className={`tab-pane fade ${activeTab === tab.id ? "show active" : ""}`}
        id={tab.id}
        role="tabpanel"
        aria-labelledby={tab.id}
      >
        {tab.children}
      </div>
    )
  }

  return (
    <div className="tab-container" id={id}>
      {/* Desktop tabs - visible on larger screens */}
      <ul className="nav nav-tabs mb-4 d-none d-md-flex">{desktopTabs}</ul>

      {/* Mobile dropdown - visible on smaller screens */}
      <div className="tab-dropdown-container d-md-none mb-3" ref={dropdownRef}>
        <button
          className="btn btn-outline-primary w-100 text-start d-flex justify-content-between align-items-center"
          onClick={() => {
            setIsDropdownOpen(!isDropdownOpen)
          }}
          type="button"
          aria-haspopup="true"
          aria-expanded={isDropdownOpen}
        >
          <div className="d-flex align-items-center">
            <span>{activeTabTitle}</span>
            {activeTabData.errorCount > 0 && (
              <span
                className="error-indicator ms-2"
                title={`Form has ${activeTabData.errorCount} error${activeTabData.errorCount > 1 ? "s" : ""}`}
                aria-live="polite"
              ></span>
            )}
          </div>
          <i className={`fas fa-chevron-${isDropdownOpen ? "up" : "down"} ms-2`}></i>
        </button>

        {isDropdownOpen && <div className="tab-dropdown-menu">{dropdownTabs}</div>}
      </div>

      <div className="tab-content">{tabPanels}</div>
    </div>
  )
}

export default TabContainer
